import { default as React, useEffect, useState } from 'react';
import {
  Entity,
  CompoundEntityRef,
  getCompoundEntityRef,
} from '@backstage/catalog-model';
import {
  useEntity,
  catalogApiRef,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import {
  InfoCard,
  Table,
  TableColumn,
  ResponseErrorPanel,
} from '@backstage/core-components';

const CARD_TITLE = 'Internal Tools & Ungrouped Components';

type ComponentRow = {
  entityRef: CompoundEntityRef;
  name: string;
  type: string;
  lifecycle: string;
  description: string;
};

const columns: TableColumn<ComponentRow>[] = [
  {
    title: 'Name',
    field: 'name',
    render: row => <EntityRefLink entityRef={row.entityRef} title={row.name} />,
  },
  { title: 'Type', field: 'type' },
  { title: 'Lifecycle', field: 'lifecycle' },
  { title: 'Description', field: 'description' },
];

function toRow(entity: Entity): ComponentRow {
  const spec = (entity.spec ?? {}) as Record<string, unknown>;
  return {
    entityRef: getCompoundEntityRef(entity),
    name: entity.metadata.title || entity.metadata.name,
    type: typeof spec.type === 'string' ? spec.type : '',
    lifecycle: typeof spec.lifecycle === 'string' ? spec.lifecycle : '',
    description: entity.metadata.description || '',
  };
}

/**
 * Lists Components that are linked directly to this Domain (Business Unit) via
 * `spec.domain` and have no `spec.system` — i.e. internal tools, POCs, and
 * ungrouped repositories onboarded through the "Other Internal Tools" path.
 *
 * These components are not surfaced by the built-in Systems card (which lists
 * systems via the `hasPart` relation), so domain owners would otherwise have no
 * visibility into them.
 *
 * The card hides itself entirely when there are no such components, so the
 * Domain page is unchanged for domains that only own structured solutions.
 */
export const DomainComponentsCard = ({ variant }: { variant?: 'gridItem' }) => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const domainName = entity.metadata.name;

  const [rows, setRows] = useState<ComponentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    catalogApi
      .getEntities({
        filter: { kind: 'Component', 'spec.domain': domainName },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
          'spec.type',
          'spec.lifecycle',
          'spec.system',
        ],
      })
      .then(response => {
        if (cancelled) return;
        // Backstage filters cannot express field-absence, so exclude
        // components that also carry a spec.system client-side.
        const internalTools = response.items.filter(
          item => !(item.spec as Record<string, unknown> | undefined)?.system,
        );
        setRows(internalTools.map(toRow));
        setError(undefined);
      })
      .catch(err => {
        if (!cancelled) setError(err as Error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [catalogApi, domainName]);

  if (error) {
    return (
      <InfoCard title={CARD_TITLE} variant={variant}>
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  // Hide the section while loading and when there are no matching components,
  // so the Domain page never shows an empty card frame (AC #4).
  if (loading || rows.length === 0) {
    return null;
  }

  return (
    <Table<ComponentRow>
      title={CARD_TITLE}
      columns={columns}
      data={rows}
      options={{
        search: rows.length > 5,
        paging: rows.length > 10,
        pageSize: 10,
        padding: 'dense',
      }}
    />
  );
};
